// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The mini-map, and the tapped nudge (M13-23, 11 §3.1).
//
// "A mouse beats hand tracking" was the objection, and this is the answer the recommendation
// gave: the mouse's job on the canvas — an exact position — is done on the bench by snapping
// (M13-13) and by **nudge**: a plan view of the machine floating beside the bench, painted by
// the same `paint()` the 2-D canvas is (as Test play's `BallMap` already is), with eight arrows
// under it — a pad of four for **1 mm** and a pad of four for **10 mm** — and a fingertip pushed
// down through an arrow moves the selected part by exactly that. Not a drag, not a slide: a
// count of taps is a count of millimetres, which is what the Builder's arrow keys are.
//
// **Where it floats.** Beside the playfield, past its edge, on the side away from the inspector
// card (M13-22) — the card takes the side with more playfield, so the two never share one — and
// on the left when there is no card. `MAP_LIFT` over the surface, as the card is, and drawn at
// `1 / scale` so it is the same map in the room at every bench scale, which is what makes every
// arrow a `TARGET` of sixteen millimetres.
//
// **A run of taps is one undo**, under `nudge:<id>` — five taps of the 1 mm arrow are one thing
// the author did, as a run of edits to one field is on the canvas — and a nudge the validator
// would refuse is not made (*Not placed: …*), as a release is.
//
// Pure: fingertips in the playfield's frame in, the arrow tapped out. `MiniMap.cs` is this
// file, case for case.

import {type EditorPart as MachineDevice,devicePosition} from '../../builder/parts'
import {   type MachineLayer, type Point2 } from '../../../core/ts/machine'
import { fitViewport, toPlayfield, type PlayfieldSize, type Viewport } from '../../builder/viewport'
import { TapGate, TOUCH_BEHIND, type TapState } from './tap'
import type { CarryHand } from './partCarry'
import { TARGET } from './inspector'
import { HoldRepeat } from './testTools'

/** The map, room metres: a hand wide, the plan above the pads. */
export const MAP_WIDTH = 0.14
export const MAP_PADDING = 0.006
/** The plan's area, room metres, one to two: a playfield fits it by height, or by width when wider than that. */
export const PLAN_WIDTH = 0.11
export const PLAN_HEIGHT = 0.22
/**
 * The plan's canvas, pixels: the 2-D Builder's `paint()` draws into it in the browser and
 * `MiniMapArt.Plan` rasterises into one the same size on the headset, both fitted with the
 * Builder's `fitViewport`. The plan's room size is the same one to two, so a pixel is square.
 */
export const PLAN_CANVAS = { width: 512, height: 1024 } as const
/** How near a fingertip's tap on the plan must be to a part's handle to choose the part, room metres on the plan: half a target. */
export const PLAN_CATCH = TARGET / 2
/** One arrow's cell; the arrow's face is `TARGET` inside it. */
export const PAD_CELL = 0.02
/** The two pads' centres, either side of the middle. */
export const PAD_APART = 0.034
/** The gap between the plan and the pads. */
const PAD_GAP = 0.004
/** How far above the surface the map floats, room metres: the inspector card's. */
export const MAP_LIFT = 0.06
/** From the playfield's edge to the map's near edge, room metres. */
export const MAP_BESIDE = 0.03
/** How far above and below the map's plane a fingertip is "at" it. */
const MAP_ABOVE = 0.05
const MAP_SLACK = 0.008

/** The map's height: the plan, the pads, and the padding around them. */
export const MAP_HEIGHT = MAP_PADDING * 2 + PLAN_HEIGHT + PAD_GAP + PAD_CELL * 3

export interface NudgeArrow {
  /** `x-1`, `x+10`, `y-1`, … — the axis, the sign and the millimetres. */
  readonly key: string
  /** The move, playfield metres. */
  readonly dx: number
  readonly dy: number
  readonly mm: number
  /** The arrow's centre, in the map's frame (room metres, origin at the centre, +y up, +x right). */
  readonly x: number
  readonly y: number
}

/** The eight arrows: a 1 mm pad on the left, a 10 mm pad on the right, each a cross of four. */
export const NUDGE_ARROWS: readonly NudgeArrow[] = (() => {
  const arrows: NudgeArrow[] = []
  const padY = -MAP_HEIGHT / 2 + MAP_PADDING + PAD_CELL * 1.5
  for (const [mm, padX] of [
    [1, -PAD_APART],
    [10, PAD_APART],
  ] as const) {
    const m = mm / 1000
    arrows.push({ key: `x-${mm}`, dx: -m, dy: 0, mm, x: padX - PAD_CELL, y: padY })
    arrows.push({ key: `x+${mm}`, dx: m, dy: 0, mm, x: padX + PAD_CELL, y: padY })
    arrows.push({ key: `y+${mm}`, dx: 0, dy: m, mm, x: padX, y: padY + PAD_CELL })
    arrows.push({ key: `y-${mm}`, dx: 0, dy: -m, mm, x: padX, y: padY - PAD_CELL })
  }
  return arrows
})()

/** What the plan is painted from: the machine as the draft has it, and what to light. */
export interface PlanSource {
  readonly devices: readonly MachineDevice[]
  readonly layers: readonly MachineLayer[] | undefined
  readonly selected: string | undefined
  /** The chosen ramp or wire, when no part is selected (26 Sep 2026): the plan zooms onto it too. */
  readonly selectedLayer?: string | undefined
}

export interface MiniMapView {
  /** The map's centre, playfield metres. */
  readonly at: Point2
  /** −1 left of the playfield, +1 right of it. */
  readonly side: -1 | 1
  /** The map's size, room metres. */
  readonly width: number
  readonly height: number
  /** The plan's centre in the map's frame, and its size, room metres. */
  readonly plan: { readonly x: number; readonly y: number; readonly width: number; readonly height: number }
  readonly playfield: PlayfieldSize
  readonly source: PlanSource
  /** The arrows are live: a part is selected to nudge. Drawn dim otherwise. */
  readonly enabled: boolean
  /**
   * How much nearer the plan is than the whole machine (the zoom follow-up): 1 with nothing
   * selected — the whole playfield, fitted — and `PLAN_ZOOM` with a part selected, centred on it.
   */
  readonly planZoom: number
  /** The playfield point at the plan's centre: the origin, or the selected part held inside the playfield. */
  readonly planCentre: Point2
  /** Changes when the plan should be repainted: the machine, the selection, the side. */
  readonly key: string
}

/** How much nearer the plan comes with a part selected: its neighbours, not the whole cabinet. */
export const PLAN_ZOOM = 2.5

/** Room around a chosen ramp or wire on the zoomed plan, playfield metres, so its ends are not on the edge. */
export const LAYER_MARGIN = 0.03

/**
 * The zoom and the point to centre on for a chosen ramp or wire (26 Sep 2026: "zoom the map onto a
 * chosen wire"): the middle of its control points' footprint, and as near as `PLAN_ZOOM` while
 * the whole footprint — with `LAYER_MARGIN` round it — still fits on the plan; the whole
 * playfield (1) for one longer than that. Nothing for no such layer.
 */
function layerFocus(playfield: PlayfieldSize, source: PlanSource): { readonly zoom: number; readonly at: Point2 } | undefined {
  const id = source.selectedLayer
  if (id === undefined) return undefined
  const layer = (source.layers ?? []).find((l) => l.id === id)
  if (layer === undefined || (layer.kind !== 'ramp' && layer.kind !== 'wire') || layer.spline.length === 0) return undefined
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const [x, y] of layer.spline) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }
  const fit = fitViewport(playfield, PLAN_CANVAS.width, PLAN_CANVAS.height)
  const across = PLAN_CANVAS.width / (fit.pixelsPerMetre * (maxX - minX + 2 * LAYER_MARGIN))
  const along = PLAN_CANVAS.height / (fit.pixelsPerMetre * (maxY - minY + 2 * LAYER_MARGIN))
  const zoom = Math.min(PLAN_ZOOM, Math.max(1, Math.min(across, along)))
  return { zoom, at: [(minX + maxX) / 2, (minY + maxY) / 2] }
}

/**
 * The plan's own viewport: the Builder's `fitViewport` for the whole playfield, brought
 * `planZoom` nearer and moved to `planCentre`. The painter draws with it and a tap is read with
 * it, so a tap lands on what is drawn at any zoom.
 */
export function planViewport(view: MiniMapView): Viewport {
  const fit = fitViewport(view.playfield, PLAN_CANVAS.width, PLAN_CANVAS.height)
  return { ...fit, pixelsPerMetre: fit.pixelsPerMetre * view.planZoom, centre: view.planCentre }
}

/**
 * Where the zoomed plan centres for a selected part: its position, held in so the plan never
 * shows more than a margin of the rails' outside.
 */
function planCentreFor(playfield: PlayfieldSize, zoom: number, at: Point2): Point2 {
  const fit = fitViewport(playfield, PLAN_CANVAS.width, PLAN_CANVAS.height)
  const ppm = fit.pixelsPerMetre * zoom
  const halfW = PLAN_CANVAS.width / 2 / ppm
  const halfH = PLAN_CANVAS.height / 2 / ppm
  const limitX = Math.max(0, playfield.width / 2 - halfW)
  const limitY = Math.max(0, playfield.length / 2 - halfH)
  return [Math.min(limitX, Math.max(-limitX, at[0])), Math.min(limitY, Math.max(-limitY, at[1]))]
}

/**
 * The map for this frame.
 *
 * `version` is the host's count of definitions the draft has been through — the picture's
 * repaint key, because a definition has no cheap fingerprint. `cardX` is where the inspector
 * card is, playfield metres, or nothing: the map takes the other side. `enabled` is whether the
 * arrows are live: a part selected and none in hand.
 */
export function miniMapViewOf(
  playfield: PlayfieldSize,
  scale: number,
  source: PlanSource,
  version: number,
  cardX: number | undefined,
  enabled: boolean = source.selected !== undefined,
): MiniMapView {
  const s = scale > 0 ? scale : 1
  const side: -1 | 1 = cardX === undefined ? -1 : cardX > 0 ? -1 : 1
  const x = side * (playfield.width / 2 + (MAP_BESIDE + MAP_WIDTH / 2) / s)
  const plan = { x: 0, y: MAP_HEIGHT / 2 - MAP_PADDING - PLAN_HEIGHT / 2, width: PLAN_WIDTH, height: PLAN_HEIGHT }
  // Zoomed to the selection: the part and its neighbours, where a millimetre is visible.
  const chosen = source.selected === undefined ? undefined : source.devices.find((d) => d.id === source.selected)
  const where = chosen === undefined ? undefined : devicePosition(chosen)
  // …or to a chosen ramp or wire, as near as its whole length allows.
  const layer = where === undefined ? layerFocus(playfield, source) : undefined
  const planZoom = where !== undefined ? PLAN_ZOOM : layer !== undefined ? layer.zoom : 1
  const planCentre: Point2 =
    where !== undefined ? planCentreFor(playfield, planZoom, where) : layer !== undefined ? planCentreFor(playfield, planZoom, layer.at) : [0, 0]
  const chosenKey = source.selected ?? (layer !== undefined ? `layer:${source.selectedLayer ?? ''}` : '-')
  return {
    at: [x, 0],
    side,
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    plan,
    playfield,
    source,
    enabled,
    planZoom,
    planCentre,
    key: `${version}|${chosenKey}|${side}|${Number(planZoom.toFixed(6))}|${planCentre[0].toFixed(4)},${planCentre[1].toFixed(4)}`,
  }
}

/**
 * The playfield point under a place on the plan (M13-23 follow-up: tap-to-select). `u`, `v` are
 * room metres from the plan's centre, +v up the plan; the answer is the painter's own
 * `toPlayfield` on the painter's own `fitViewport`, so a tap lands on what is drawn there.
 */
export function planToPlayfield(view: MiniMapView, u: number, v: number): Point2 {
  const fit = planViewport(view)
  const px = (u / view.plan.width + 0.5) * PLAN_CANVAS.width
  const py = (0.5 - v / view.plan.height) * PLAN_CANVAS.height
  return toPlayfield(fit, px, py)
}

/** `PLAN_CATCH` in playfield metres: how far from a part's handle a tap on the plan still chooses it. */
export function planCatchMetres(view: MiniMapView): number {
  const fit = planViewport(view)
  const roomPerMetre = (fit.pixelsPerMetre * view.plan.width) / PLAN_CANVAS.width
  return roomPerMetre > 0 ? PLAN_CATCH / roomPerMetre : 0
}

interface Local {
  readonly x: number
  readonly y: number
  readonly z: number
}

function toMap(view: MiniMapView, hand: CarryHand, scale: number): Local {
  return { x: (hand.x - view.at[0]) * scale, y: (hand.y - view.at[1]) * scale, z: hand.z * scale - MAP_LIFT }
}

function onMap(view: MiniMapView, local: Local): boolean {
  return (
    Math.abs(local.x) <= view.width / 2 + MAP_SLACK &&
    Math.abs(local.y) <= view.height / 2 + MAP_SLACK &&
    local.z <= MAP_ABOVE &&
    local.z >= -TOUCH_BEHIND
  )
}

const ARROW = { width: TARGET, height: TARGET }

export interface MiniMapFrame {
  /** The arrow tapped this frame, or nothing. One per frame: the first hand's. */
  readonly nudged: NudgeArrow | undefined
  /**
   * The playfield point under a fingertip pushed down through the plan this frame, or nothing
   * (tap-to-select). One per frame: the first hand's. Live with or without a selection — choosing
   * one is what it is for.
   */
  readonly picked: Point2 | undefined
  /** Hands over the map — masked from the carry, so an arrow is never a handle. */
  readonly over: { readonly left: boolean; readonly right: boolean }
}

const NO_FRAME: MiniMapFrame = { nudged: undefined, picked: undefined, over: { left: false, right: false } }

export class MiniMapInput {
  private readonly gates = new Map<string, Record<'left' | 'right', TapGate>>()
  /** The plan's own gate per hand: the whole plan is one face. */
  private planGates: Record<'left' | 'right', TapGate> | undefined
  /**
   * An arrow held down repeats (the map follow-up after M13-23): the press is the first
   * millimetre, then after `REPEAT_DELAY_MS` ten a second while the finger stays on it — the Step
   * row's `HoldRepeat`, so both held controls on the bench feel the same. Per hand, with the arrow it
   * is holding.
   */
  private readonly holds = { left: new HoldRepeat(), right: new HoldRepeat() }
  private readonly holding: Record<'left' | 'right', string | undefined> = { left: undefined, right: undefined }

  /**
   * One frame of fingertips in the playfield's frame. `carrying` is true while a part is in a
   * hand, and then no arrow is tapped — a hand carrying a part over the map is carrying.
   */
  advance(
    view: MiniMapView | undefined,
    tips: readonly CarryHand[],
    scale: number,
    carrying: boolean,
    frameMs = 0,
  ): MiniMapFrame {
    if (view === undefined) {
      this.release()
      return NO_FRAME
    }
    const s = scale > 0 ? scale : 1
    const over = { left: false, right: false }
    const down: Record<'left' | 'right', NudgeArrow | undefined> = { left: undefined, right: undefined }
    let nudged: NudgeArrow | undefined
    for (const arrow of NUDGE_ARROWS) {
      let gates = this.gates.get(arrow.key)
      if (gates === undefined) {
        gates = { left: new TapGate(ARROW), right: new TapGate(ARROW) }
        this.gates.set(arrow.key, gates)
      }
      for (const hand of tips) {
        const gate = gates[hand.handedness]
        let state: TapState
        if (!hand.present || carrying || hand.grab || hand.atPlinth !== undefined) {
          state = gate.release()
        } else {
          const local = toMap(view, hand, s)
          if (onMap(view, local)) over[hand.handedness] = true
          state = gate.advance({ x: local.x - arrow.x, y: local.y - arrow.y, z: local.z })
        }
        // A tap with nothing selected is a tap on nothing: the pad is dim, and it says so.
        if (state.pressedEdge && view.enabled && nudged === undefined) nudged = arrow
        if (state.down && down[hand.handedness] === undefined) down[hand.handedness] = arrow
      }
    }
    // Held arrows repeat. A hand that moves to another arrow starts that one's delay afresh; a hand
    // that lets go, or a dim pad, repeats nothing. One nudge a frame, as a tap is.
    for (const side of ['left', 'right'] as const) {
      const held = down[side]
      if (held === undefined || !view.enabled || held.key !== this.holding[side]) {
        this.holds[side].release()
        this.holding[side] = held !== undefined && view.enabled ? held.key : undefined
        if (held !== undefined && view.enabled) this.holds[side].advance(true, frameMs)
        continue
      }
      const repeats = this.holds[side].advance(true, frameMs)
      if (repeats > 0 && nudged === undefined) nudged = held
    }
    // The plan: a fingertip pushed down through it anywhere is a tap on the playfield point drawn
    // there. The host decides what that chooses.
    let picked: Point2 | undefined
    if (this.planGates === undefined) {
      const face = { width: view.plan.width, height: view.plan.height }
      this.planGates = { left: new TapGate(face), right: new TapGate(face) }
    }
    for (const hand of tips) {
      const gate = this.planGates[hand.handedness]
      if (!hand.present || carrying || hand.grab || hand.atPlinth !== undefined) {
        gate.release()
        continue
      }
      const local = toMap(view, hand, s)
      const u = local.x - view.plan.x
      const v = local.y - view.plan.y
      const state = gate.advance({ x: u, y: v, z: local.z })
      if (state.pressedEdge && picked === undefined) picked = planToPlayfield(view, u, v)
    }
    return { nudged, picked, over }
  }

  /** Every gate let go — the mode is leaving, the hands were reassigned. */
  release(): void {
    for (const gates of this.gates.values()) {
      gates.left.release()
      gates.right.release()
    }
    this.gates.clear()
    this.holds.left.release()
    this.holds.right.release()
    this.holding.left = undefined
    this.holding.right = undefined
    this.planGates?.left.release()
    this.planGates?.right.release()
    this.planGates = undefined
  }
}
